import React, { useState } from 'react';
import { 
  FileText, Sparkles, Check, X, Edit2, RefreshCw,
  ChevronDown, ChevronUp, Globe, Rocket, AlertCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { apiCallJson } from '../utils/api';

export default function AdCreator({ initialKeyword = '', initialLanguage = 'nl' }) {
  const { theme } = useTheme();
  const [keyword, setKeyword] = useState(initialKeyword);
  const [selectedLanguages, setSelectedLanguages] = useState([initialLanguage]);
  const [businessInfo, setBusinessInfo] = useState({ website: '', usps: '' });
  const [generatedAds, setGeneratedAds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('nl');
  const [editMode, setEditMode] = useState({});
  const [approvedLanguages, setApprovedLanguages] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [showCampaignSetup, setShowCampaignSetup] = useState(false);
  const [campaignData, setCampaignData] = useState({
    campaignName: '',
    adGroupName: '',
    budget: 10,
    finalUrl: ''
  });
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  
  // New enhanced features state
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [landingPageData, setLandingPageData] = useState(null);
  const [analyzeCompetitors, setAnalyzeCompetitors] = useState(true);
  const [competitorCount, setCompetitorCount] = useState(3);
  const [descriptionTypes, setDescriptionTypes] = useState(['informative', 'urgency', 'commercial', 'strong-cta']);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Add to existing campaign state
  const [campaignMode, setCampaignMode] = useState('new'); // 'new' or 'existing'
  const [existingCampaigns, setExistingCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaignAdGroups, setCampaignAdGroups] = useState([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState('');
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const availableLanguages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'da', name: 'Dansk', flag: '🇩🇰' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
    { code: 'ro', name: 'Română', flag: '🇷🇴' },
    { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'hu', name: 'Magyar', flag: '🇭🇺' }
  ];

  const toggleLanguage = (code) => {
    setSelectedLanguages(prev => 
      prev.includes(code) 
        ? prev.filter(l => l !== code)
        : [...prev, code]
    );
  };

  const generateAds = async () => {
    if (!keyword) return;

    setLoading(true);
    setPushResult(null);
    setLandingPageData(null);
    
    try {
      const data = await apiCallJson('/api/ads/generate-multilang', {
        method: 'POST',
        timeout: 180000, // multi-language generation can take a while
        body: {
          keyword,
          languages: selectedLanguages,
          businessInfo,
          landingPageUrl: landingPageUrl || businessInfo.website || null,
          analyzeCompetitors,
          competitorCount,
          sourceLanguage: initialLanguage || 'nl',
          descriptionTypes
        }
      });
      if (data.success) {
        setGeneratedAds(data);
        setActiveTab(selectedLanguages[0]);
        // Auto-suggest campaign name
        setCampaignData(prev => ({
          ...prev,
          campaignName: `${keyword} Campaign`,
          adGroupName: `${keyword} Ad Group`
        }));
      }
    } catch (error) {
      console.error('Error generating ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrapeLandingPage = async () => {
    const url = landingPageUrl || businessInfo.website;
    if (!url) return;

    setLoading(true);
    try {
      const data = await apiCallJson('/api/scrape/landing-page', {
        method: 'POST',
        timeout: 60000,
        body: { url }
      });
      if (data.success) {
        setLandingPageData(data);
      }
    } catch (error) {
      console.error('Error scraping landing page:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDescriptionType = (type) => {
    setDescriptionTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : prev.length < 4
        ? [...prev, type]
        : [...prev.slice(1), type]
    );
  };

  const regenerateForLanguage = async (lang) => {
    setLoading(true);
    try {
      const data = await apiCallJson('/api/ads/generate-multilang', {
        method: 'POST',
        timeout: 120000,
        body: {
          keyword,
          languages: [lang],
          businessInfo,
          sourceLanguage: initialLanguage || 'nl'
        }
      });
      if (data.success && data.data[lang]) {
        setGeneratedAds(prev => ({
          ...prev,
          data: {
            ...prev.data,
            [lang]: data.data[lang]
          }
        }));
      }
    } catch (error) {
      console.error('Error regenerating ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveLanguage = (lang) => {
    setApprovedLanguages(prev => ({ ...prev, [lang]: true }));
    setEditMode(prev => ({ ...prev, [lang]: false }));
  };

  const allLanguagesApproved = () => {
    return selectedLanguages.every(lang => approvedLanguages[lang]);
  };

  const fetchExistingCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const data = await apiCallJson('/api/campaigns');
      if (data.success && data.campaigns) {
        setExistingCampaigns(data.campaigns);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const fetchAdGroups = async (campaignId) => {
    setLoadingCampaigns(true);
    try {
      const data = await apiCallJson(`/api/campaigns/${campaignId}/adgroups`);
      if (data.success && data.adGroups) {
        setCampaignAdGroups(data.adGroups);
      }
    } catch (error) {
      console.error('Error fetching ad groups:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaign(campaignId);
    setSelectedAdGroup('');
    setCampaignAdGroups([]);
    if (campaignId) {
      fetchAdGroups(campaignId);
    }
  };

  const pushToGoogleAds = async () => {
    if (!allLanguagesApproved()) return;
    
    if (campaignMode === 'existing' && (!selectedCampaign || !selectedAdGroup)) {
      alert('Please select both a campaign and an ad group');
      return;
    }

    setPushing(true);
    const results = [];

    for (const lang of selectedLanguages) {
      if (!approvedLanguages[lang]) continue;

      const langData = generatedAds.data[lang];
      
      try {
        if (campaignMode === 'new') {
          // Create new campaign
          const data = await apiCallJson('/api/campaigns/create-with-ads', {
            method: 'POST',
            timeout: 60000,
            body: {
              campaignName: `${campaignData.campaignName} - ${langData.language}`,
              adGroupName: campaignData.adGroupName,
              budget: campaignData.budget,
              language: lang,
              country: langData.country,
              ads: {
                headlines: langData.headlines,
                descriptions: langData.descriptions,
                finalUrl: campaignData.finalUrl
              },
              keywords: [{ keyword: langData.translatedKeyword }]
            }
          });
          results.push({
            language: langData.language,
            flag: langData.flag,
            success: data.success,
            data: data.data,
            error: data.error,
            mode: 'new'
          });
        } else {
          // Add to existing campaign
          const data = await apiCallJson(`/api/campaigns/${selectedCampaign}/adgroups/${selectedAdGroup}/keywords`, {
            method: 'POST',
            timeout: 60000,
            body: {
              keywords: [{ 
                keyword: langData.translatedKeyword,
                matchType: 'BROAD'
              }]
            }
          });
          results.push({
            language: langData.language,
            flag: langData.flag,
            success: data.success,
            data: data.data,
            error: data.error,
            mode: 'existing'
          });
        }
      } catch (error) {
        results.push({
          language: langData.language,
          flag: langData.flag,
          success: false,
          error: error.message,
          mode: campaignMode
        });
      }
    }

    setPushResult(results);
    setPushing(false);
  };

  const getCharCountColor = (text, max) => {
    const length = text?.length || 0;
    if (length > max) return '#ef4444';
    if (length > max * 0.9) return '#f59e0b';
    return '#10b981';
  };

  return (
    <div style={{ padding: 40 }}>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <Sparkles size={36} color="#8b5cf6" />
          <h1 style={{ color: theme.colors.text.primary, fontSize: 32, fontWeight: 700, margin: 0 }}>
            AI Ad Creator
          </h1>
        </div>
        <p style={{ color: theme.colors.text.tertiary, fontSize: 16 }}>
          Generate compelling Google Ads with AI - 15 headlines, 4 descriptions & extensions
        </p>
      </div>

      {/* Input Section */}
      {!generatedAds && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 20,
          padding: 30,
          marginBottom: 40,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
            Ad Configuration
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Keyword Input */}
            <div>
              <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                Target Keyword *
              </label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g., laptop, running shoes, insurance..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 16
                }}
              />
            </div>

            {/* Language Selection */}
            <div>
              <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                Target Languages *
              </label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    style={{
                      padding: '8px 16px',
                      background: selectedLanguages.includes(lang.code) 
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
                        : 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 10,
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    {lang.flag} {lang.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Business Info */}
            <div>
              <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                Website (Optional)
              </label>
              <input
                type="text"
                value={businessInfo.website}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>

            <div>
              <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                USPs / Key Benefits (Optional)
              </label>
              <textarea
                value={businessInfo.usps}
                onChange={(e) => setBusinessInfo(prev => ({ ...prev, usps: e.target.value }))}
                placeholder="e.g., Free shipping, 30-day returns, expert support..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Advanced Options Toggle */}
            <div>
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  padding: '10px 16px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  justifyContent: 'space-between'
                }}
              >
                <span>⚡ Advanced Options (Competitor Analysis, Landing Page, Description Types)</span>
                {showAdvancedOptions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            </div>

            {/* Advanced Options Panel */}
            {showAdvancedOptions && (
              <div style={{
                background: 'rgba(0, 0, 0, 0.2)',
                padding: 20,
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: 15
              }}>
                {/* Landing Page URL */}
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Landing Page URL (auto-filled from website)
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="text"
                      value={landingPageUrl || businessInfo.website}
                      onChange={(e) => setLandingPageUrl(e.target.value)}
                      placeholder="https://example.com/product"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 8,
                        color: 'white',
                        fontSize: 13
                      }}
                    />
                    <button
                      onClick={scrapeLandingPage}
                      disabled={loading || !(landingPageUrl || businessInfo.website)}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.5)',
                        borderRadius: 8,
                        color: '#60a5fa',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 13,
                        fontWeight: 500
                      }}
                    >
                      Preview
                    </button>
                  </div>
                  {landingPageData && landingPageData.data && (
                    <div style={{
                      marginTop: 10,
                      padding: 10,
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'rgba(255, 255, 255, 0.8)'
                    }}>
                      ✓ Landing page scraped: {landingPageData.data.content.usps.length} USPs, {landingPageData.data.images.length} images
                    </div>
                  )}
                </div>

                {/* Competitor Analysis */}
                <div>
                  <label style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: 13, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8 
                  }}>
                    <input
                      type="checkbox"
                      checked={analyzeCompetitors}
                      onChange={(e) => setAnalyzeCompetitors(e.target.checked)}
                      style={{ width: 16, height: 16 }}
                    />
                    Analyze Competitor Ads
                  </label>
                  {analyzeCompetitors && (
                    <div style={{ marginLeft: 24 }}>
                      <label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 12, display: 'block', marginBottom: 6 }}>
                        Number of competitors to analyze
                      </label>
                      <input
                        type="number"
                        value={competitorCount}
                        onChange={(e) => setCompetitorCount(parseInt(e.target.value) || 3)}
                        min={1}
                        max={10}
                        style={{
                          width: 80,
                          padding: '6px 10px',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: 6,
                          color: 'white',
                          fontSize: 13
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Description Types */}
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Description Types (select exactly 4)
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {[
                      { value: 'informative', label: 'Informative' },
                      { value: 'urgency', label: 'Urgency' },
                      { value: 'commercial', label: 'Commercial' },
                      { value: 'strong-cta', label: 'Strong CTA' },
                      { value: 'emotional', label: 'Emotional' },
                      { value: 'social-proof', label: 'Social Proof' },
                      { value: 'problem-solution', label: 'Problem-Solution' }
                    ].map(type => (
                      <button
                        key={type.value}
                        onClick={() => toggleDescriptionType(type.value)}
                        disabled={!descriptionTypes.includes(type.value) && descriptionTypes.length >= 4}
                        style={{
                          padding: '6px 12px',
                          background: descriptionTypes.includes(type.value)
                            ? 'rgba(139, 92, 246, 0.3)'
                            : 'rgba(255, 255, 255, 0.05)',
                          border: descriptionTypes.includes(type.value)
                            ? '1px solid rgba(139, 92, 246, 0.5)'
                            : '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 6,
                          color: descriptionTypes.includes(type.value) ? 'white' : 'rgba(255, 255, 255, 0.6)',
                          cursor: (!descriptionTypes.includes(type.value) && descriptionTypes.length >= 4) ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          textAlign: 'left'
                        }}
                      >
                        {descriptionTypes.includes(type.value) ? '✓ ' : ''}{type.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.5)', marginTop: 6 }}>
                    Selected: {descriptionTypes.length}/4
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={generateAds}
              disabled={loading || !keyword || selectedLanguages.length === 0}
              style={{
                background: loading || !keyword || selectedLanguages.length === 0
                  ? 'rgba(139, 92, 246, 0.3)'
                  : 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                border: 'none',
                color: 'white',
                padding: '14px 32px',
                borderRadius: 12,
                cursor: loading || !keyword || selectedLanguages.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                marginTop: 10
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Generating with AI...
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  Generate Ads
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Preview Section */}
      {generatedAds && !showCampaignSetup && (
        <div>
          {/* Language Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: 10, 
            marginBottom: 20,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            paddingBottom: 10
          }}>
            {selectedLanguages.map(lang => {
              const langData = generatedAds.data[lang];
              if (!langData) return null;
              
              return (
                <button
                  key={lang}
                  onClick={() => setActiveTab(lang)}
                  style={{
                    padding: '10px 20px',
                    background: activeTab === lang 
                      ? 'rgba(139, 92, 246, 0.2)'
                      : 'transparent',
                    border: activeTab === lang 
                      ? '1px solid rgba(139, 92, 246, 0.5)'
                      : '1px solid transparent',
                    borderRadius: 10,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  {langData.flag} {langData.language}
                  {approvedLanguages[lang] && (
                    <Check size={16} style={{ color: '#10b981' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Active Language Ad Preview */}
          {generatedAds.data[activeTab] && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 20,
              padding: 30,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: 20
            }}>
              {/* Header with Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                    <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, margin: 0 }}>
                      {generatedAds.data[activeTab].language} Ads
                    </h3>
                    {generatedAds.data[activeTab].usedCompetitorData && (
                      <span style={{
                        background: 'rgba(245, 158, 11, 0.2)',
                        color: '#fbbf24',
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600
                      }} title={`Analyzed ${generatedAds.data[activeTab].competitorCount} competitors`}>
                        🔍 Competitor Data
                      </span>
                    )}
                    {generatedAds.data[activeTab].usedLandingPageData && (
                      <span style={{
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa',
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600
                      }} title="Used landing page content">
                        🌐 Page Data
                      </span>
                    )}
                  </div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 13, margin: 0 }}>
                    Keyword: {generatedAds.data[activeTab].translatedKeyword}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setEditMode(prev => ({ ...prev, [activeTab]: !prev[activeTab] }))}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                      borderRadius: 8,
                      color: '#60a5fa',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <Edit2 size={14} />
                    {editMode[activeTab] ? 'View' : 'Edit'}
                  </button>
                  <button
                    onClick={() => regenerateForLanguage(activeTab)}
                    disabled={loading}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(245, 158, 11, 0.2)',
                      border: '1px solid rgba(245, 158, 11, 0.5)',
                      borderRadius: 8,
                      color: '#fbbf24',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <RefreshCw size={14} />
                    Regenerate
                  </button>
                  <button
                    onClick={() => approveLanguage(activeTab)}
                    disabled={approvedLanguages[activeTab]}
                    style={{
                      padding: '8px 16px',
                      background: approvedLanguages[activeTab] 
                        ? 'rgba(16, 185, 129, 0.3)'
                        : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      border: 'none',
                      borderRadius: 8,
                      color: 'white',
                      cursor: approvedLanguages[activeTab] ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <Check size={14} />
                    {approvedLanguages[activeTab] ? 'Approved' : 'Approve'}
                  </button>
                </div>
              </div>

              {/* Headlines */}
              <div style={{ marginBottom: 25 }}>
                <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                  Headlines ({generatedAds.data[activeTab].headlines.length}/15)
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10 }}>
                  {generatedAds.data[activeTab].headlines.map((headline, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 11 }}>H{idx + 1}</span>
                        <span style={{ 
                          color: getCharCountColor(headline, 30), 
                          fontSize: 11,
                          fontWeight: 600
                        }}>
                          {headline.length}/30
                        </span>
                      </div>
                      <p style={{ color: 'white', fontSize: 13, margin: 0 }}>{headline}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Descriptions */}
              <div style={{ marginBottom: 25 }}>
                <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                  Descriptions ({generatedAds.data[activeTab].descriptions.length}/4)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {generatedAds.data[activeTab].descriptions.map((desc, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '12px 14px',
                      borderRadius: 8,
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 11 }}>D{idx + 1}</span>
                        <span style={{ 
                          color: getCharCountColor(desc, 90), 
                          fontSize: 11,
                          fontWeight: 600
                        }}>
                          {desc.length}/90
                        </span>
                      </div>
                      <p style={{ color: 'white', fontSize: 14, margin: 0 }}>{desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Extensions (Collapsible) */}
              <div>
                <button
                  onClick={() => setExpandedSections(prev => ({ ...prev, [activeTab]: !prev[activeTab] }))}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 10,
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  <span>Ad Extensions (Sitelinks, Callouts, Snippets)</span>
                  {expandedSections[activeTab] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {expandedSections[activeTab] && (
                  <div style={{ marginTop: 15, padding: 15, background: 'rgba(0, 0, 0, 0.2)', borderRadius: 10 }}>
                    {/* Sitelinks */}
                    <h5 style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Sitelinks</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 15 }}>
                      {generatedAds.data[activeTab].sitelinks?.map((link, idx) => (
                        <div key={idx} style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: 8,
                          borderRadius: 6,
                          fontSize: 12
                        }}>
                          <div style={{ color: '#60a5fa', fontWeight: 600 }}>{link.title}</div>
                          <div style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{link.description}</div>
                        </div>
                      ))}
                    </div>

                    {/* Callouts */}
                    <h5 style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Callouts</h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 15 }}>
                      {generatedAds.data[activeTab].callouts?.map((callout, idx) => (
                        <span key={idx} style={{
                          background: 'rgba(16, 185, 129, 0.2)',
                          color: '#10b981',
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 12
                        }}>
                          {callout}
                        </span>
                      ))}
                    </div>

                    {/* Structured Snippets */}
                    <h5 style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Structured Snippets</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {generatedAds.data[activeTab].structuredSnippets?.map((snippet, idx) => (
                        <div key={idx} style={{ fontSize: 12 }}>
                          <span style={{ color: '#a78bfa', fontWeight: 600 }}>{snippet.header}: </span>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{snippet.values.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div>
              <p style={{ color: 'white', fontSize: 14, margin: 0, marginBottom: 4 }}>
                {Object.keys(approvedLanguages).length} / {selectedLanguages.length} languages approved
              </p>
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 12, margin: 0 }}>
                Approve all languages to proceed to campaign setup
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => {
                  setGeneratedAds(null);
                  setApprovedLanguages({});
                  setEditMode({});
                }}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Start Over
              </button>
              <button
                onClick={() => setShowCampaignSetup(true)}
                disabled={!allLanguagesApproved()}
                style={{
                  padding: '10px 24px',
                  background: allLanguagesApproved()
                    ? 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
                    : 'rgba(139, 92, 246, 0.3)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'white',
                  cursor: allLanguagesApproved() ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                Continue to Campaign Setup
                <Rocket size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Setup */}
      {showCampaignSetup && generatedAds && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 20,
          padding: 30,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
            Campaign Setup
          </h3>

          {/* Campaign Mode Selector */}
          <div style={{ marginBottom: 25 }}>
            <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, display: 'block', marginBottom: 12 }}>
              Campaign Mode
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setCampaignMode('new')}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: campaignMode === 'new' 
                    ? 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: campaignMode === 'new' 
                    ? 'none'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                🆕 Create New Campaign
              </button>
              <button
                onClick={() => {
                  setCampaignMode('existing');
                  if (existingCampaigns.length === 0) {
                    fetchExistingCampaigns();
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: campaignMode === 'existing' 
                    ? 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: campaignMode === 'existing' 
                    ? 'none'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                ➕ Add to Existing Campaign
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginBottom: 25 }}>
            {campaignMode === 'existing' ? (
              <>
                {/* Existing Campaign Selector */}
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                    Select Campaign
                  </label>
                  <select
                    value={selectedCampaign}
                    onChange={(e) => handleCampaignSelect(e.target.value)}
                    disabled={loadingCampaigns}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 8,
                      color: 'white',
                      fontSize: 14
                    }}
                  >
                    <option value="">-- Select a campaign --</option>
                    {existingCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCampaign && (
                  <div>
                    <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                      Select Ad Group
                    </label>
                    <select
                      value={selectedAdGroup}
                      onChange={(e) => setSelectedAdGroup(e.target.value)}
                      disabled={loadingCampaigns}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 8,
                        color: 'white',
                        fontSize: 14
                      }}
                    >
                      <option value="">-- Select an ad group --</option>
                      {campaignAdGroups.map(adGroup => (
                        <option key={adGroup.id} value={adGroup.id}>
                          {adGroup.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: 15,
                  borderRadius: 10,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'flex',
                  gap: 10
                }}>
                  <AlertCircle size={20} style={{ color: '#60a5fa', flexShrink: 0 }} />
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, margin: 0 }}>
                    Keywords from generated ads will be added to the selected ad group.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* New Campaign Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={campaignData.campaignName}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, campaignName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                  Ad Group Name
                </label>
                <input
                  type="text"
                  value={campaignData.adGroupName}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, adGroupName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                  Daily Budget (€)
                </label>
                <input
                  type="number"
                  value={campaignData.budget}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, budget: parseFloat(e.target.value) }))}
                  min="1"
                  step="1"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                  Final URL
                </label>
                <input
                  type="text"
                  value={campaignData.finalUrl}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, finalUrl: e.target.value }))}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
            </div>

            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              padding: 15,
              borderRadius: 10,
              border: '1px solid rgba(59, 130, 246, 0.3)',
              display: 'flex',
              gap: 10
            }}>
              <AlertCircle size={20} style={{ color: '#60a5fa', flexShrink: 0 }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, margin: 0 }}>
                Campaigns will be created in PAUSED state for safety. You can review and activate them in Google Ads.
              </p>
            </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCampaignSetup(false)}
              disabled={pushing}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 10,
                color: 'white',
                cursor: pushing ? 'not-allowed' : 'pointer',
                fontSize: 14
              }}
            >
              Back to Ads
            </button>
            <button
              onClick={pushToGoogleAds}
              disabled={
                pushing || 
                (campaignMode === 'new' && (!campaignData.campaignName || !campaignData.adGroupName)) ||
                (campaignMode === 'existing' && (!selectedCampaign || !selectedAdGroup))
              }
              style={{
                padding: '12px 32px',
                background: (
                  pushing || 
                  (campaignMode === 'new' && (!campaignData.campaignName || !campaignData.adGroupName)) ||
                  (campaignMode === 'existing' && (!selectedCampaign || !selectedAdGroup))
                ) ? 'rgba(16, 185, 129, 0.3)' : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                border: 'none',
                borderRadius: 10,
                color: 'white',
                cursor: (
                  pushing || 
                  (campaignMode === 'new' && (!campaignData.campaignName || !campaignData.adGroupName)) ||
                  (campaignMode === 'existing' && (!selectedCampaign || !selectedAdGroup))
                ) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {pushing ? (
                <>
                  <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  {campaignMode === 'new' ? 'Creating Campaigns...' : 'Adding Keywords...'}
                </>
              ) : (
                <>
                  <Rocket size={18} />
                  {campaignMode === 'new' 
                    ? `Create ${selectedLanguages.length} Campaign${selectedLanguages.length > 1 ? 's' : ''}`
                    : `Add Keywords to Ad Group`
                  }
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {pushResult && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 20,
          padding: 30,
          marginTop: 20,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
            Campaign Creation Results
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pushResult.map((result, idx) => (
              <div key={idx} style={{
                background: result.success 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${result.success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                borderRadius: 12,
                padding: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                {result.success ? (
                  <Check size={24} style={{ color: '#10b981' }} />
                ) : (
                  <X size={24} style={{ color: '#ef4444' }} />
                )}
                <div style={{ flex: 1 }}>
                  <p style={{ color: 'white', fontSize: 14, fontWeight: 600, margin: 0, marginBottom: 4 }}>
                    {result.flag} {result.language}
                  </p>
                  {result.success ? (
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 13, margin: 0 }}>
                      {result.mode === 'new' 
                        ? `Campaign created successfully (Status: ${result.data?.status || 'N/A'})`
                        : `Keywords added successfully (${result.data?.count || 0} keyword${result.data?.count !== 1 ? 's' : ''})`
                      }
                    </p>
                  ) : (
                    <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>
                      Error: {result.error}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              setPushResult(null);
              setGeneratedAds(null);
              setShowCampaignSetup(false);
              setApprovedLanguages({});
            }}
            style={{
              marginTop: 20,
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
              border: 'none',
              borderRadius: 10,
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            Create New Ads
          </button>
        </div>
      )}
    </div>
  );
}

